import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Paper,
  Alert
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';

/**
 * ImageUploader Component
 * Kullanıcıların lokal görsel yüklemesini sağlar
 */
const ImageUploader = ({ 
  imageUrl = '', 
  onImageChange = () => {},
  disabled = false 
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [previewUrl, setPreviewUrl] = useState(imageUrl);
  const [error, setError] = useState('');

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) {
      setError('Lütfen geçerli bir görsel dosyası seçin (JPG, PNG, vb.)');
      return;
    }

    // Dosya boyutu kontrolü (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Görsel boyutu 5MB\'dan küçük olmalıdır');
      return;
    }

    setError('');

    // Dosyayı base64'e çevir
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setPreviewUrl(base64String);
      onImageChange(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    setPreviewUrl('');
    onImageChange('');
    setError('');
  };

  return (
    <Box>
      <Typography
        variant="subtitle1"
        sx={{
          mb: 1,
          color: colors.greenAccent[400],
          fontWeight: 'bold'
        }}
      >
        📷 Görsel Yükleme
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!previewUrl ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: colors.primary[600],
            border: `2px dashed ${colors.blueAccent[500]}`,
            borderRadius: 2,
            cursor: disabled ? 'default' : 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': disabled ? {} : {
              borderColor: colors.greenAccent[500],
              backgroundColor: colors.primary[500]
            }
          }}
        >
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="image-upload-input"
            type="file"
            onChange={handleFileSelect}
            disabled={disabled}
          />
          <label htmlFor="image-upload-input">
            <Box sx={{ cursor: disabled ? 'default' : 'pointer' }}>
              <CloudUploadIcon 
                sx={{ 
                  fontSize: 64, 
                  color: colors.blueAccent[400],
                  mb: 2
                }} 
              />
              <Typography variant="h6" sx={{ color: colors.grey[100], mb: 1 }}>
                Görsel Yükle
              </Typography>
              <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                Bir görsel seçmek için tıklayın veya sürükleyin
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[500], mt: 1, display: 'block' }}>
                Desteklenen formatlar: JPG, PNG, GIF (Maks. 5MB)
              </Typography>
            </Box>
          </label>
        </Paper>
      ) : (
        <Paper
          sx={{
            p: 2,
            backgroundColor: colors.primary[600],
            border: `2px solid ${colors.greenAccent[500]}`,
            borderRadius: 2
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                width: '100%',
                maxHeight: 400,
                objectFit: 'contain',
                borderRadius: 8
              }}
            />
            {!disabled && (
              <IconButton
                onClick={handleRemove}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: colors.redAccent[600],
                  color: 'white',
                  '&:hover': {
                    backgroundColor: colors.redAccent[700]
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
          <Typography 
            variant="caption" 
            sx={{ 
              mt: 1, 
              color: colors.grey[400],
              display: 'block',
              textAlign: 'center'
            }}
          >
            ✅ Görsel yüklendi
          </Typography>
        </Paper>
      )}

      <Box sx={{ mt: 2, p: 1.5, backgroundColor: colors.blueAccent[900], borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: colors.blueAccent[300] }}>
          💡 <strong>İpucu:</strong> Eklem kıkırdağı haritası için net ve yüksek çözünürlüklü bir görsel kullanın.
          Görsel yükledikten sonra alanları tanımlayabilirsiniz.
        </Typography>
      </Box>
    </Box>
  );
};

export default ImageUploader;




